/* @jsxImportSource @emotion/react */
import { css } from '@emotion/react/macro'
import React, { useCallback, useState } from 'react'
import styled from '@emotion/styled'
import { Radio, Button, Tree, Checkbox } from 'antd'
import { CloseOutlined } from '@ant-design/icons'
import Text from 'src/components/ts-components/Text'
import colors from '@sportninja/common/constants/appColors'
import { paragraphSmall, weightBold, weightRegular } from 'src/components/css'
import { Team } from '@sportninja/common/hooks/useSearchAdminTeams'
import { getImageThumbnailId } from '@sportninja/common/utils/utils'
import { MappedSport } from '@sportninja/common/hooks/useNewSports'
import { PlayerListModal } from './PlayerListModal'
import OfficialListModal from './OfficialListModal'
import { Link } from 'react-router-dom'
import { ROUTES } from '@sportninja/common/constants/app'

interface SelectedProperties {
  [key: string]: {
    teamId: string
    value: string
  }
}

interface MergeTeamSelectorProps {
  teams: Team[]
  onTeamSelect: (teamId: string) => void
  selectedTeamId?: string
  onRemoveTeam: (teamId: string) => void
  findSport: (sportId: string, genericSportId: string) => MappedSport | null
  onPropertySelect?: (property: string, teamId: string, value: string) => void
  selectedProperties: SelectedProperties
  requiredProperties?: string[]
}

interface TreeNode {
  key: string
  title: React.ReactNode
  children?: TreeNode[]
}

const Container = styled.div<{ teams: number }>`
  display: grid;
  width: 100%;
  gap: 24px;

  ${({ teams }) => {
    if (teams === 1) {
      return `
        grid-template-columns: minmax(0, 1fr);
        max-width: 33.33%;
        margin: 0 auto;
      `
    }
    if (teams === 2) {
      return `
        grid-template-columns: repeat(2, minmax(0, 33.33%));
        justify-content: center;
        gap: 48px;
      `
    }
    return `
      grid-template-columns: repeat(3, minmax(0, 33.33%));
      justify-content: space-between;
    `
  }}

  @media (max-width: 1366px) {
    ${({ teams }) => {
      if (teams === 1) {
        return `
          grid-template-columns: minmax(0, 1fr);
          max-width: 50%;
          margin: 0 auto;
        `
      }
      return `
        grid-template-columns: repeat(2, minmax(0, 50%));
        justify-content: center;
        gap: 24px;
      `
    }}
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    max-width: 100%;
    margin: 0;
    gap: 24px;
  }
`

const TeamCard = styled.div`
  width: 100%;
  border-radius: 8px;
  border: 1px solid #27303e;
  overflow: hidden;
`

const TeamHeader = styled.div`
  padding: 8px;
  background: ${colors.SECONDARY_500};
  display: flex;
  align-items: center;
  gap: 8px;
`

const TeamInfo = styled.div`
  padding: 16px;
  background: ${colors.SECONDARY_300};
  display: flex;
  align-items: center;
  gap: 16px;
`

const TeamLogo = styled.img`
  width: 48px;
  height: 48px;
  border-radius: 8px;
  object-fit: contain;
`

const TeamName = styled.span`
  margin: 0;
`

const InfoRow = styled.div<{ alternate?: boolean; isSelected?: boolean }>`
  padding: 8px 16px;
  gap: 16px;
  display: grid;
  grid-template-columns: auto auto 1fr;
  align-items: flex-start;
  background: ${(props) =>
    props.isSelected
      ? colors.SECONDARY_200
      : props.alternate
      ? colors.SECONDARY_400
      : colors.SECONDARY_500};

  > div {
    min-width: 0;
  }
`

const labelStyles = css`
  ${paragraphSmall}
  ${weightBold}
  color: ${colors.NEUTRAL_0};
`

const Label = styled.div`
  ${labelStyles}
  text-transform: capitalize;
`

const Value = styled.div`
  ${labelStyles}
  ${weightRegular}
  word-break: break-word;
  text-align: right;
  padding-right: 8px;
`

const CompetitionRow = styled.div<{
  alternate?: boolean
  header?: boolean
  isHierarchy?: boolean
}>`
  padding: 8px 16px;
  display: grid;
  width: 100%;
  box-sizing: border-box;
  grid-template-columns: ${(props) =>
    props.isHierarchy ? '2fr 1fr' : 'minmax(0, 2fr) 80px 80px 110px'};
  gap: 8px;
  align-items: center;
  min-height: 40px;
  background: ${(props) =>
    props.header
      ? colors.SECONDARY_300
      : props.alternate
      ? colors.SECONDARY_500
      : colors.SECONDARY_400};

  > * {
    min-width: 0;
  }
`

const RemoveButton = styled(Button)`
  min-width: 24px;
  width: 24px;
  height: 24px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  &.ant-btn {
    background: transparent;
    border: none;
    color: #797b7e;

    &:hover {
      color: white;
      background: rgba(255, 255, 255, 0.1);
    }
  }
`

const TruncatedText = styled.div`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
  line-height: 1.2;
  min-height: 32px;
  display: flex;
  align-items: center;
`

const PlayerCount = styled.div<{ hasPlayers: boolean }>`
  ${({ hasPlayers }) =>
    hasPlayers &&
    `
    cursor: pointer;
    &:hover {
      text-decoration: underline;
    }
  `}
  color: ${({ hasPlayers }) =>
    hasPlayers ? colors.NEUTRAL_0 : colors.NEUTRAL_70};
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
`

const OfficialCount = styled.div<{ hasOfficials: boolean }>`
  ${({ hasOfficials }) =>
    hasOfficials &&
    `
    cursor: pointer;
    &:hover {
      text-decoration: underline;
    }
  `}
  color: ${({ hasOfficials }) =>
    hasOfficials ? colors.NEUTRAL_0 : colors.NEUTRAL_70};
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  white-space: nowrap;
  min-width: 110px;
`

const AffiliateCount = styled.div<{ hasAffiliates: boolean }>`
  ${({ hasAffiliates }) =>
    hasAffiliates &&
    `
    cursor: pointer;
    &:hover {
      text-decoration: underline;
    }
  `}
  color: ${({ hasAffiliates }) =>
    hasAffiliates ? colors.NEUTRAL_0 : colors.NEUTRAL_70};
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
`

const StyledLink = styled(Link)`
  text-decoration: none;
  color: ${colors.NEUTRAL_0};
  &:hover {
    text-decoration: underline;
  }
  height: 32px;
  display: flex;
  align-items: center;
  padding: 0 0px;
`

const TreeContainer = styled.div`
  padding: 8px 16px;
  background: ${colors.SECONDARY_400};

  .ant-tree {
    background: transparent;
    color: ${colors.NEUTRAL_0};
  }

  .ant-tree .ant-tree-node-content-wrapper {
    color: ${colors.NEUTRAL_0};
    min-height: 24px;
    display: flex;
    align-items: center;
    padding: 0 2px;
  }

  .ant-tree .ant-tree-node-content-wrapper:hover {
    background-color: ${colors.SECONDARY_500};
  }

  .ant-tree .ant-tree-node-content-wrapper.ant-tree-node-selected {
    background-color: ${colors.SECONDARY_500};
  }

  .ant-tree-show-line .ant-tree-indent-unit::before {
    border-color: ${colors.NEUTRAL_70};
  }

  .ant-tree-switcher {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    line-height: 24px;
    height: 24px;
  }

  .ant-tree-switcher .ant-tree-switcher-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    vertical-align: top;
  }

  .ant-tree-title {
    display: flex;
    align-items: center;
    height: 24px;
  }

  .ant-tree-treenode {
    display: flex;
    align-items: center;
    padding: 0;
    width: 100%;
  }

  .ant-tree-node-content-wrapper {
    line-height: 24px;
  }

  .ant-tree-indent,
  .ant-tree-switcher {
    align-self: center;
  }
`

const createTreeData = (hierarchy: any[]): TreeNode[] => {
  if (!hierarchy?.length) {
    return []
  }

  // Start with the first item (top level)
  const root: TreeNode = {
    key: hierarchy[0].uid,
    title: (
      <StyledLink to={`${ROUTES.ORG_ROOT}/${hierarchy[0].uid}`} target='_blank'>
        <Text
          variant='paragraphSmall'
          weight='regular'
          color={colors.NEUTRAL_0}
        >
          {hierarchy[0].name}
        </Text>
      </StyledLink>
    ),
    children: [],
  }

  // Build the tree from top to bottom
  let currentNode: TreeNode = root
  for (let i = 1; i < hierarchy.length; i++) {
    const newNode: TreeNode = {
      key: hierarchy[i].uid,
      title: (
        <StyledLink
          to={`${ROUTES.ORG_ROOT}/${hierarchy[i].uid}`}
          target='_blank'
        >
          <Text
            variant='paragraphSmall'
            weight='regular'
            color={colors.NEUTRAL_0}
          >
            {hierarchy[i].name}
          </Text>
        </StyledLink>
      ),
      children: [],
    }
    currentNode.children = [newNode]
    currentNode = newNode
  }

  return [root]
}

export const MergeTeamSelector = ({
  teams,
  onTeamSelect,
  selectedTeamId,
  onRemoveTeam,
  findSport,
  onPropertySelect,
  selectedProperties,
}: MergeTeamSelectorProps) => {
  const [selectedRoster, setSelectedRoster] = useState<any>(null)
  const [isPlayerModalVisible, setIsPlayerModalVisible] = useState(false)
  const [isOfficialModalVisible, setIsOfficialModalVisible] = useState(false)
  const [isAffiliateModalVisible, setIsAffiliateModalVisible] = useState(false)

  const handleTeamSelect = useCallback(
    (e: any) => {
      onTeamSelect(e.target.value)
    },
    [onTeamSelect]
  )

  const handleRemoveTeam = useCallback(
    (teamId: string, e: React.MouseEvent) => {
      e.stopPropagation()
      onRemoveTeam(teamId)
    },
    [onRemoveTeam]
  )

  const handlePropertySelect = useCallback(
    (property: string, teamId: string, value: string, e: any) => {
      e?.stopPropagation()
      onPropertySelect?.(property, teamId, value)
    },
    [onPropertySelect]
  )

  const handleAffiliateCountClick = useCallback((roster: any) => {
    const affiliateCount =
      roster?.players?.filter((player: any) => player?.is_affiliate)?.length ||
      0
    if (affiliateCount > 0) {
      setSelectedRoster({
        ...roster,
        players:
          roster?.players?.filter((player: any) => player?.is_affiliate) || [],
      })
      setIsAffiliateModalVisible(true)
    }
  }, [])

  const handlePlayerCountClick = useCallback((roster: any) => {
    const nonAffiliateCount =
      roster?.players?.filter((player: any) => !player?.is_affiliate)?.length ||
      0
    if (nonAffiliateCount > 0) {
      setSelectedRoster({
        ...roster,
        players:
          roster?.players?.filter((player: any) => !player?.is_affiliate) || [],
      })
      setIsPlayerModalVisible(true)
    }
  }, [])

  const handlePlayerModalClose = useCallback(() => {
    setIsPlayerModalVisible(false)
    setSelectedRoster(null)
  }, [])

  const handleOfficialModalClose = useCallback(() => {
    setIsOfficialModalVisible(false)
    setSelectedRoster(null)
  }, [])

  const handleAffiliateModalClose = useCallback(() => {
    setIsAffiliateModalVisible(false)
    setSelectedRoster(null)
  }, [])

  const handleOfficialCountClick = useCallback((roster: any, team: Team) => {
    if (roster?.team_officials_count > 0) {
      setSelectedRoster({
        ...roster,
        sport_id: team?.sport_id,
      })
      setIsOfficialModalVisible(true)
    }
  }, [])

  const renderPropertyRow = (
    label: string,
    value: string | undefined,
    teamId: string,
    property: string,
    alternate: boolean
  ) => {
    const isSelected = selectedProperties[property]?.teamId === teamId

    return (
      <InfoRow alternate={alternate} isSelected={isSelected}>
        <Checkbox
          checked={isSelected}
          onChange={(e) =>
            handlePropertySelect(property, teamId, value || '', e)
          }
          disabled={!value}
        />
        <Label>{label}</Label>
        <Value>{value || '-'}</Value>
      </InfoRow>
    )
  }

  const renderTeamDetails = (team: Team) => (
    <TeamCard key={team?.id}>
      <TeamHeader>
        <Radio value={team?.id} />
        <div style={{ flex: 1 }}>
          <Text
            variant='paragraphSmall'
            weight='regular'
            color={colors.NEUTRAL_0}
          >
            Keep {team?.name || 'Invalid Team Name'}
          </Text>
        </div>
        <RemoveButton
          icon={
            <CloseOutlined
              css={css`
                font-size: 24px;
                color: ${colors.NEUTRAL_100};
              `}
            />
          }
          onClick={(e) => handleRemoveTeam(team?.id, e)}
        />
      </TeamHeader>

      <TeamInfo>
        <TeamLogo
          src={
            getImageThumbnailId(team) ||
            '/images/generic_placeholders/placeholder_team.png'
          }
          alt={team?.name || 'Invalid Team Name'}
        />
        <Link to={`${ROUTES.TEAM_ROOT}/${team?.id}`} target='_blank'>
          <TeamName>
            <Text
              variant='paragraphMedium'
              weight='bold'
              color={colors.NEUTRAL_0}
            >
              {team?.name || 'Invalid Team Name'}
            </Text>
          </TeamName>
        </Link>
      </TeamInfo>

      <InfoRow alternate>
        <div />
        <Label>Team ID</Label>
        <Value>{team?.id || 'Invalid Team ID'}</Value>
      </InfoRow>

      <InfoRow>
        <div />
        <Label>Sport</Label>
        <Value>
          {findSport(team?.sport_id, team?.generic_sport_id || '')?.name_full ||
            'Invalid Sport'}
        </Value>
      </InfoRow>

      {team?.daysmart_id &&
        renderPropertyRow(
          'DaySmart ID',
          team.daysmart_id,
          team.id,
          'daysmart_id',
          true
        )}

      {team?.salesforce_id &&
        renderPropertyRow(
          'Salesforce ID',
          team.salesforce_id,
          team.id,
          'salesforce_id',
          true
        )}

      {renderPropertyRow(
        'Email',
        team?.email || undefined,
        team?.id,
        'email',
        true
      )}
      {renderPropertyRow(
        'Phone',
        team?.phone || undefined,
        team?.id,
        'phone',
        false
      )}
      {renderPropertyRow(
        'Website',
        team?.website || undefined,
        team?.id,
        'website',
        true
      )}
      <CompetitionRow header>
        <Text
          variant='paragraphSmall'
          weight='regular'
          color={colors.NEUTRAL_70}
          style={'text-transform: uppercase;'}
        >
          COMPETITIONS
        </Text>
        <Text
          variant='paragraphSmall'
          weight='regular'
          color={colors.NEUTRAL_70}
          style={'text-transform: uppercase;'}
        >
          PLAYERS
        </Text>
        <Text
          variant='paragraphSmall'
          weight='regular'
          color={colors.NEUTRAL_70}
          style={'text-transform: uppercase;'}
        >
          AFFILIATES
        </Text>
        <Text
          variant='paragraphSmall'
          weight='regular'
          color={colors.NEUTRAL_70}
          style={'text-transform: uppercase; white-space: nowrap;'}
        >
          TEAM OFFICIALS
        </Text>
      </CompetitionRow>

      {team?.rosters?.length > 0 ? (
        team?.rosters?.map((roster, index) => {
          const playersCount = roster?.players_count || 0
          const affiliateCount =
            roster?.players?.filter((player: any) => player?.is_affiliate)
              ?.length || 0
          return (
            <CompetitionRow key={roster?.id} alternate={index % 2 === 1}>
              <StyledLink
                to={`${ROUTES.SCHEDULE_ROOT}/${roster.schedule_uid}`}
                target='_blank'
              >
                <TruncatedText>
                  <Text
                    variant='paragraphSmall'
                    weight='regular'
                    color={colors.NEUTRAL_0}
                  >
                    {roster?.schedule}
                  </Text>
                </TruncatedText>
              </StyledLink>
              <PlayerCount
                hasPlayers={playersCount > 0}
                onClick={() => handlePlayerCountClick(roster)}
              >
                <Text
                  variant='paragraphSmall'
                  weight='regular'
                  color={
                    playersCount > 0 ? colors.NEUTRAL_0 : colors.NEUTRAL_70
                  }
                >
                  {playersCount || '0'}
                </Text>
              </PlayerCount>
              <AffiliateCount
                hasAffiliates={affiliateCount > 0}
                onClick={() => handleAffiliateCountClick(roster)}
              >
                <Text
                  variant='paragraphSmall'
                  weight='regular'
                  color={
                    affiliateCount > 0 ? colors.NEUTRAL_0 : colors.NEUTRAL_70
                  }
                >
                  {affiliateCount || '0'}
                </Text>
              </AffiliateCount>
              <OfficialCount
                hasOfficials={roster.team_officials_count > 0}
                onClick={() => handleOfficialCountClick(roster, team)}
              >
                <Text
                  variant='paragraphSmall'
                  weight='regular'
                  color={
                    roster.team_officials_count > 0
                      ? colors.NEUTRAL_0
                      : colors.NEUTRAL_70
                  }
                >
                  {roster.team_officials_count || '0'}
                </Text>
              </OfficialCount>
            </CompetitionRow>
          )
        })
      ) : (
        <CompetitionRow>
          <div>
            <Text
              variant='paragraphSmall'
              weight='regular'
              color={colors.NEUTRAL_70}
            >
              -
            </Text>
          </div>
          <PlayerCount hasPlayers={false}>
            <Text
              variant='paragraphSmall'
              weight='regular'
              color={colors.NEUTRAL_70}
            >
              0
            </Text>
          </PlayerCount>
          <AffiliateCount hasAffiliates={false}>
            <Text
              variant='paragraphSmall'
              weight='regular'
              color={colors.NEUTRAL_70}
            >
              0
            </Text>
          </AffiliateCount>
          <OfficialCount hasOfficials={false}>
            <Text
              variant='paragraphSmall'
              weight='regular'
              color={colors.NEUTRAL_70}
            >
              0
            </Text>
          </OfficialCount>
        </CompetitionRow>
      )}
      {team?.hierarchy?.length > 0 && (
        <>
          <CompetitionRow header>
            <Text
              variant='paragraphSmall'
              weight='regular'
              color={colors.NEUTRAL_70}
              style={'text-transform: uppercase;'}
            >
              PARENT ORGANIZATIONS
            </Text>
          </CompetitionRow>
          {team?.hierarchy?.length === 1 ? (
            <CompetitionRow>
              <StyledLink
                to={`${ROUTES.ORG_ROOT}/${team?.hierarchy[0]?.uid}`}
                target='_blank'
              >
                <Text
                  variant='paragraphSmall'
                  weight='regular'
                  color={colors.NEUTRAL_0}
                >
                  {team.hierarchy[0].name}
                </Text>
              </StyledLink>
            </CompetitionRow>
          ) : (
            <TreeContainer>
              <Tree
                showLine
                defaultExpandAll
                selectable={false}
                treeData={createTreeData(team?.hierarchy)}
              />
            </TreeContainer>
          )}
        </>
      )}
    </TeamCard>
  )

  return (
    <>
      <Container teams={teams.length}>
        <Radio.Group
          onChange={handleTeamSelect}
          value={selectedTeamId}
          style={{ width: '100%', display: 'contents' }}
        >
          {teams && teams?.map((team) => renderTeamDetails(team))}
        </Radio.Group>
      </Container>

      <PlayerListModal
        roster={selectedRoster}
        isVisible={isPlayerModalVisible}
        onClose={handlePlayerModalClose}
        modalTitle='Players'
      />

      <PlayerListModal
        roster={selectedRoster}
        isVisible={isAffiliateModalVisible}
        onClose={handleAffiliateModalClose}
        modalTitle='Affiliate Players'
      />

      <OfficialListModal
        roster={selectedRoster}
        isVisible={isOfficialModalVisible}
        onClose={handleOfficialModalClose}
      />
    </>
  )
}
